import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/site/Seo";
import { UK_HERO_IMG } from "@/data/projects";

const ABOUT_IMG = UK_HERO_IMG; // standalone hero image (not in the projects gallery)

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Prime Build Design — 10+ Years in London"
        description="Prime Build Design LTD is a domestic construction company based in Wembley Park, London. A decade of new builds, renovations and extensions across the capital with in-house teams and a 10-year structural guarantee."
        path="/about"
      />
      {/* Hero */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-6">About Prime Build Design</div>
            <h1 className="font-display text-5xl md:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05]">
              A decade of building<br /> with intent.
            </h1>
          </div>
          <p className="lg:col-span-5 text-slate-600 text-lg leading-relaxed">
            We founded Prime Build Design in 2014 to challenge a simple frustration in residential
            construction — that homeowners shouldn't have to choose between craftsmanship,
            transparency, and timely delivery. Ten years on, we still won't.
          </p>
        </div>
      </section>

      {/* Big numbers */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid md:grid-cols-4 gap-px bg-slate-200">
          {[
            ["10+", "Years in business"],
            ["120+", "Homes delivered"],
            ["£42M+", "Built in value"],
            ["98%", "Client referral rate"],
          ].map(([n, l], i) => (
            <div key={l} className="bg-white p-10" data-testid={`about-stat-${i}`}>
              <div className="font-display text-6xl font-bold text-slate-900 leading-none">{n}</div>
              <div className="overline mt-6">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-2 gap-16 items-center">
          <div className="zoom-img order-2 lg:order-1">
            <img src={ABOUT_IMG} alt="Architect blueprints" className="w-full h-[560px] object-cover" loading="lazy" decoding="async" width="1200" height="800" />
          </div>
          <div className="order-1 lg:order-2">
            <div className="overline mb-4">Our story</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
              Quiet rigour.<br /> Visible results.
            </h2>
            <div className="mt-8 space-y-5 text-slate-600 leading-relaxed">
              <p>
                Founded in London by a small group of architects and master builders, Prime Build
                Design was born from a single principle: a home should be built with the same care
                with which it will be lived in.
              </p>
              <p>
                Today our in-house teams cover every stage — design coordination, structural works,
                joinery, MEP and finishing — eliminating the gaps where projects usually slow down or
                overrun.
              </p>
              <p>
                We're not the biggest builder in the city. By choice. We take on a deliberate number
                of projects each year so that every client gets a dedicated lead, a transparent
                weekly report, and a finished home worth waiting for.
              </p>
            </div>
            <Link to="/contact" className="btn-accent mt-10" data-testid="about-cta-consult">
              Book a Consultation <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="overline mb-4">What we stand for</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight max-w-3xl leading-tight">
            Four principles that shape every nail we drive.
          </h2>

          <div className="mt-16 grid md:grid-cols-2 gap-px bg-slate-200">
            {[
              ["01", "Transparency", "Weekly reports, locked-in budgets, and a single point of contact from day one."],
              ["02", "Craftsmanship", "In-house joinery and finishing teams. Sub-millimetre tolerances. Every. Single. Time."],
              ["03", "Stewardship", "Of your home, your budget, and the trades on site. We protect all three."],
              ["04", "Guarantee", "Every project carries a 10-year structural guarantee and a 2-year finishes warranty."],
            ].map(([n, t, d], i) => (
              <div key={t} className="bg-white p-10" data-testid={`about-value-${i}`}>
                <div className="font-display text-[#C5A059] text-sm font-bold">{n}</div>
                <h3 className="mt-4 font-display text-2xl font-semibold text-slate-900">{t}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
